import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Images } from "lucide-react";
import { BrandLockup } from "@/components/BrandLockup";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import { useUnreadNotifications } from "@/hooks/useUnreadNotifications";
import { useActionCounts } from "@/hooks/useActionCounts";
import {
  LayoutDashboard,
  BarChart3,
  Building2,
  Package,
  Warehouse,
  Ship,
  CalendarClock,
  ShoppingCart,
  BadgeDollarSign,
  Palette,
  FlaskConical,
  FileCheck,
  Sparkles,
  Bell,
  Mail,
  ShieldCheck,
  LogOut,
  Menu,
  Flag,
  Mic,
  BookOpen,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/reservations", label: "Reservations", icon: CalendarClock },
  { to: "/admin/samples", label: "Samples", icon: FlaskConical },
  { to: "/admin/certificates", label: "Certificates", icon: FileCheck },
  { to: "/admin/custom-studio", label: "Custom Studio", icon: Sparkles },
  { to: "/admin/media", label: "Media Library", icon: Images },
  { to: "/admin/inventory", label: "Inventory", icon: Warehouse },
  { to: "/admin/containers", label: "Containers", icon: Ship },
  { to: "/admin/suppliers", label: "Suppliers", icon: Ship },
  { to: "/admin/sourcing", label: "Sourcing", icon: Package },
  { to: "/admin/dealers", label: "Dealers", icon: Building2 },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/catalog", label: "Colors & Variations", icon: Palette },
  { to: "/admin/knowledge", label: "Technical Knowledge", icon: BookOpen },
  { to: "/admin/pricing", label: "Pricing", icon: BadgeDollarSign },
  { to: "/admin/partner-program", label: "Partner Program", icon: BadgeDollarSign },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/email-log", label: "Email Log", icon: Mail },
  { to: "/admin/reports", label: "Reports", icon: Flag },
  { to: "/admin/voice-telemetry", label: "ZELI Voice", icon: Mic },
  { to: "/admin/test-data", label: "Test Data", icon: ShieldCheck },
];

const ACTION_BADGES = {
  Certificates: "certificates_pending",
  "Custom Studio": "custom_requests_action",
  Samples: "samples_action",
  Reservations: "reservations_pending",
  Orders: "orders_action",
  Reports: "reports_new",
};

function NavLinks({ onNavigate, mobile = false, unread = 0, counts = {} }) {
  return (
    <nav className="mt-8 space-y-1 px-4" data-testid={mobile ? "admin-nav-mobile" : "admin-nav"}>
      {NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onNavigate}
          data-testid={`admin-nav-${mobile ? "mobile-" : ""}${label.toLowerCase()}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
              isActive
                ? "bg-white font-medium text-charcoal shadow-sm"
                : "text-warmgrey hover:bg-[#EAE8E3] hover:text-charcoal"
            }`
          }
        >
          <Icon className="h-4 w-4" />
          {label}
          {ACTION_BADGES[label] && counts[ACTION_BADGES[label]] > 0 && (
            <span
              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta px-1.5 text-[10px] font-bold text-white"
              data-testid={`admin-action-badge${mobile ? "-mobile" : ""}-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {counts[ACTION_BADGES[label]]}
            </span>
          )}
          {label === "Notifications" && unread > 0 && (
            <span
              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-terracotta px-1.5 text-[10px] font-bold text-white"
              data-testid={mobile ? "admin-notif-badge-mobile" : "admin-notif-badge"}
            >
              {unread}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  return (
    <div className="px-7 pt-8">
      <p className="font-display text-2xl font-medium tracking-tight text-charcoal">
        <BrandLockup className="font-display text-lg font-medium text-charcoal" />
      </p>
      <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        Administration
      </p>
    </div>
  );
}

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unread = useUnreadNotifications();
  const counts = useActionCounts();

  useEffect(() => {
    // §27.3: operational sans-serif typography across the authenticated workspace
    document.body.classList.add("connect-ops");
    return () => document.body.classList.remove("connect-ops");
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen overflow-hidden bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-[280px] flex-col bg-sidebar md:flex">
        <Brand />
        <div className="flex-1 overflow-y-auto">
          <NavLinks unread={unread} counts={counts} />
        </div>
        <div className="border-t border-border p-4">
          <div className="px-3 py-2">
            <p className="text-sm font-medium text-charcoal">{user?.name}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            data-testid="admin-logout-button"
            onClick={handleLogout}
            className="mt-1 w-full justify-start gap-3 text-warmgrey hover:text-charcoal"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="flex min-h-screen min-w-0 flex-1 flex-col md:ml-[280px]">
        <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-border bg-background/80 px-6 backdrop-blur-md md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="admin-mobile-menu-button" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-[280px] flex-col bg-sidebar p-0">
              <SheetTitle className="sr-only">Administration menu</SheetTitle>
              <Brand />
              <div className="flex-1 overflow-y-auto">
                <NavLinks mobile unread={unread} counts={counts} onNavigate={() => setOpen(false)} />
              </div>
              <div className="border-t border-border p-4">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-charcoal">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <Button
                  variant="ghost"
                  data-testid="admin-logout-button-mobile"
                  onClick={handleLogout}
                  className="mt-1 w-full justify-start gap-3 text-warmgrey hover:text-charcoal"
                >
                  <LogOut className="h-4 w-4" /> Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
          <p className="font-display text-lg font-medium text-charcoal"><BrandLockup /></p>
        </header>
        <main className="min-w-0 flex-1 p-6 pb-[calc(2rem+env(safe-area-inset-bottom))] md:p-12 lg:p-16">
          <div className="page-enter mx-auto max-w-6xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
